/**
 * PhysicsEngine — Lightweight 2D physics simulation
 *
 * Framework-agnostic: no React, no DOM. Pure math.
 * Handles gravity, velocity, boundary collisions (bounce + friction),
 * angular rotation, damping, and settle detection.
 *
 * Designed for small body counts (3-12 items) with bounds-only collisions.
 * No body-to-body collisions — unnecessary for the collect mini-game.
 */

// ─── Types ───────────────────────────────────────────────────────────

export interface Vec2 {
  x: number;
  y: number;
}

export interface PhysicsBody {
  id: string;
  position: Vec2;
  velocity: Vec2;
  rotation: number;           // Degrees
  angularVelocity: number;    // Degrees per second
  width: number;
  height: number;
  restitution: number;        // Bounciness 0-1
  friction: number;           // Per-body ground friction multiplier
  settled: boolean;
}

export interface PhysicsBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface PhysicsConfig {
  gravity: number;                // px/s² (980 ≈ 1g)
  airDamping: number;             // Velocity decay coefficient per second while airborne
  groundFriction: number;         // Global friction multiplier on floor contact
  angularDamping: number;         // Rotation decay coefficient per second
  settleThreshold: number;        // px/s total velocity to mark as settled
  settleAngularThreshold: number; // deg/s to stop rotation
}

export interface PhysicsSnapshot {
  bodies: ReadonlyArray<Readonly<PhysicsBody>>;
  allSettled: boolean;
  elapsedTime: number;
}

// ─── Defaults ────────────────────────────────────────────────────────

export const DEFAULT_PHYSICS_CONFIG: PhysicsConfig = {
  gravity: 980,
  airDamping: 0.02,
  groundFriction: 0.85,
  angularDamping: 0.03,
  settleThreshold: 8,
  settleAngularThreshold: 3,
};

const MAX_DELTA_TIME = 0.05; // 50ms cap prevents spiral-of-death

// ─── Engine ──────────────────────────────────────────────────────────

export class PhysicsEngine {
  private bodies: Map<string, PhysicsBody> = new Map();
  private bounds: PhysicsBounds;
  private config: PhysicsConfig;
  private elapsedTime = 0;

  constructor(bounds: PhysicsBounds, config?: Partial<PhysicsConfig>) {
    this.bounds = bounds;
    this.config = { ...DEFAULT_PHYSICS_CONFIG, ...config };
  }

  /** Add a body to the simulation */
  addBody(body: PhysicsBody): void {
    this.bodies.set(body.id, body);
  }

  /** Remove a body by ID */
  removeBody(id: string): void {
    this.bodies.delete(id);
  }

  /** Get a body by ID */
  getBody(id: string): PhysicsBody | undefined {
    return this.bodies.get(id);
  }

  /** Update bounds (e.g., on container resize) */
  setBounds(bounds: PhysicsBounds): void {
    this.bounds = bounds;
  }

  /** Advance simulation by deltaTime seconds */
  step(deltaTime: number): void {
    const dt = Math.min(deltaTime, MAX_DELTA_TIME);
    this.elapsedTime += dt;

    for (const body of this.bodies.values()) {
      if (body.settled) continue;

      // Gravity
      body.velocity.y += this.config.gravity * dt;

      // Air damping (subtle)
      body.velocity.x *= 1 - this.config.airDamping * dt;

      // Position integration
      body.position.x += body.velocity.x * dt;
      body.position.y += body.velocity.y * dt;

      // Rotation integration
      body.rotation += body.angularVelocity * dt;

      // Angular damping
      body.angularVelocity *= 1 - this.config.angularDamping * dt;

      // Boundary collisions
      this.resolveCollisions(body);

      // Settle detection
      this.checkSettle(body);
    }
  }

  /** Get readonly snapshot of current state */
  getSnapshot(): PhysicsSnapshot {
    const bodiesArray = Array.from(this.bodies.values());
    return {
      bodies: bodiesArray,
      allSettled: bodiesArray.length > 0 && bodiesArray.every((b) => b.settled),
      elapsedTime: this.elapsedTime,
    };
  }

  /** Check if all remaining bodies have settled */
  areAllSettled(): boolean {
    if (this.bodies.size === 0) return true;
    for (const body of this.bodies.values()) {
      if (!body.settled) return false;
    }
    return true;
  }

  /** Get body count */
  getBodyCount(): number {
    return this.bodies.size;
  }

  /** Reset the simulation */
  reset(): void {
    this.bodies.clear();
    this.elapsedTime = 0;
  }

  // ─── Private ─────────────────────────────────────────────────────

  private resolveCollisions(body: PhysicsBody): void {
    const halfW = body.width / 2;
    const halfH = body.height / 2;
    const friction = this.config.groundFriction * body.friction;

    // Bottom wall (the "boat floor")
    if (body.position.y + halfH > this.bounds.bottom) {
      body.position.y = this.bounds.bottom - halfH;
      body.velocity.y = -body.velocity.y * body.restitution;
      // Floor friction: reduce horizontal speed
      body.velocity.x *= friction;
      // Small angular impulse on bounce
      body.angularVelocity += body.velocity.x * 0.08;
    }

    // Left wall
    if (body.position.x - halfW < this.bounds.left) {
      body.position.x = this.bounds.left + halfW;
      body.velocity.x = -body.velocity.x * body.restitution;
      body.angularVelocity += body.velocity.y * 0.05;
    }

    // Right wall
    if (body.position.x + halfW > this.bounds.right) {
      body.position.x = this.bounds.right - halfW;
      body.velocity.x = -body.velocity.x * body.restitution;
      body.angularVelocity += body.velocity.y * 0.05;
    }

    // Top boundary NOT enforced — items are thrown from above
  }

  private checkSettle(body: PhysicsBody): void {
    const halfH = body.height / 2;
    const onFloor = body.position.y + halfH >= this.bounds.bottom - 1; // 1px tolerance

    if (!onFloor) return;

    const totalVelocity = Math.abs(body.velocity.x) + Math.abs(body.velocity.y);
    const angularSpeed = Math.abs(body.angularVelocity);

    if (
      totalVelocity < this.config.settleThreshold &&
      angularSpeed < this.config.settleAngularThreshold
    ) {
      body.settled = true;
      body.velocity.x = 0;
      body.velocity.y = 0;
      body.angularVelocity = 0;
      // Snap to floor
      body.position.y = this.bounds.bottom - halfH;
    }
  }
}
